import React, { useContext, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { NetworkConfig } from "src/network/NetworkConfig";
import { useMutation } from "@apollo/client";
import { DELETE_AGENDAITEM_FILE_MANAGER } from "src/api/assembly";
import {
    DeleteAgendaItemFileManager,
    DeleteAgendaItemFileManagerVariables
} from "src/api/generated/DeleteAgendaItemFileManager";
import { MessageType } from "src/components/notifications/Notifier";
import { AgendaItemDocumentsListItem } from "./list-item/AgendaItemDocumentsListItem";
import styles from "./AgendaItemDocuments.module.css";

const AgendaItemDocumentsBase = () => {
    const { t } = useTranslation();
    const { uiStore, agendaitemStore } = useContext(RootStoreContext);

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const lastMutationVariables = React.useRef<DeleteAgendaItemFileManagerVariables | null>(null);

    const [deleteAgendaItemFileMutation] = useMutation<
        DeleteAgendaItemFileManager,
        DeleteAgendaItemFileManagerVariables
    >(DELETE_AGENDAITEM_FILE_MANAGER, {
        onCompleted: () => {
            const fileIdToRemove = lastMutationVariables.current?.fileid;
            if (fileIdToRemove) {
                agendaitemStore.removeAgendaItemFile(fileIdToRemove); // Call the action
                printStatusMessage(
                    t("screens.realestate.assembly.agendaitem_edit.remove_file_success"),
                    MessageType.SUCCESS
                );
            }
        },
        onError: (error) => {
            printStatusMessage(
                t("screens.realestate.assembly.agendaitem_edit.remove_file_error") + error.message,
                MessageType.ERROR
            );
        }
    });

    const onDeleteAgendaItemFile = useCallback(
        (fileid: string) => async (e: React.MouseEvent<HTMLSpanElement>) => {
            e.preventDefault();
            if (!agendaitemStore.currentAgendaItem) {
                printStatusMessage(
                    t("screens.realestate.assembly.agendaitem_edit.no_current_agenda_item"),
                    MessageType.ERROR
                );
                return;
            }

            lastMutationVariables.current = {
                fileid,
                agendaitemid: agendaitemStore.currentAgendaItem.id
            };
            await deleteAgendaItemFileMutation({
                variables: lastMutationVariables.current
            });
        },
        [deleteAgendaItemFileMutation, agendaitemStore.currentAgendaItem, printStatusMessage, t]
    );

    return (
        <div className="mb-4">
            {agendaitemStore.currentAgendaItem?.agendaitemfiles.map((agendaItemFile) => {
                const assemblyFileLink = NetworkConfig.datafileUrl + agendaItemFile.fileid;
                return (
                    <div key={agendaItemFile.fileid} className={styles.testItem}>
                        <AgendaItemDocumentsListItem
                            fileId={agendaItemFile.fileid}
                            fileName={agendaItemFile.name ?? ""}
                            fileLink={assemblyFileLink}
                            additionalIcon={
                                <>
                                    {agendaitemStore.isAgendaItemLocked ? undefined : (
                                        <UI.Icon
                                            className={styles.TrashIcon}
                                            icon={UI.SVGIcon.Trash}
                                            onClick={onDeleteAgendaItemFile(agendaItemFile.fileid)}
                                        />
                                    )}
                                </>
                            }
                        />
                    </div>
                );
            })}
        </div>
    );
};

export const AgendaItemDocuments = Sentry.withProfiler(observer(AgendaItemDocumentsBase));
